import { Controller } from "@hotwired/stimulus"

// Live per-section progress for the discovery workspace. Each section's
// badge (pinned TOC) shows "answered/total" and turns into a green check once
// every visible field in that section is filled.
export default class extends Controller {
  static targets = ["field", "badge", "navLink"]

  connect() { this.update() }

  // TOC rail click: scroll the section into view ourselves — plain #anchor
  // navigation is suppressed globally (Turbo/theme), and smooth
  // scrollIntoView silently no-ops here, so compute the position and let the
  // theme's `scroll-behavior: smooth` animate window.scrollTo. Honors the
  // section's scroll-margin-top (clearing the fixed navbar).
  jump(event) {
    const hash = event.currentTarget.getAttribute("href")
    const target = hash && document.querySelector(hash)
    if (!target) return

    event.preventDefault()
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - margin })
  }

  update() {
    const totals = {}
    this.fieldTargets.forEach((field) => {
      // Conditional ("yellow") questions hidden by discovery-reveal don't
      // count toward a section's progress until they're shown.
      if (field.classList.contains("d-none")) return
      const s = field.dataset.section
      if (!totals[s]) totals[s] = { total: 0, done: 0 }
      totals[s].total += 1
      if (this.filled(field)) totals[s].done += 1
    })

    this.badgeTargets.forEach((badge) => {
      const { total = 0, done = 0 } = totals[badge.dataset.section] || {}
      const complete = total > 0 && done === total
      badge.innerHTML = complete ? '<i class="fa-solid fa-check"></i>' : `${done}/${total}`
      badge.classList.toggle("bg-success", complete)
      badge.classList.toggle("text-white", complete)
      badge.classList.toggle("bg-secondary-subtle", !complete)
      badge.classList.toggle("text-secondary", !complete)
    })

    this.navLinkTargets.forEach((link) => {
      const { total = 0, done = 0 } = totals[link.dataset.section] || {}
      link.classList.toggle("discovery-toc-complete", total > 0 && done === total)
    })
  }

  // A question is "filled" if any of its controls has a value / is checked.
  filled(field) {
    if (Array.from(field.querySelectorAll("trix-editor")).some((el) => this.trixFilled(el))) return true

    return Array.from(field.querySelectorAll("input, select, textarea")).some((el) => {
      if (el.type === "hidden") return false
      if (el.type === "checkbox" || el.type === "radio") return el.checked
      return el.value != null && String(el.value).trim() !== ""
    })
  }

  // Trix binds to a hidden input we otherwise skip. Empty editors still emit
  // a wrapper-only snapshot (`<div><br></div>`), so count visible text only.
  trixFilled(el) {
    const probe = document.createElement("div")
    probe.innerHTML = String(el.value || "")
    return probe.textContent.trim() !== ""
  }
}
